import React, { useState, useEffect, useRef } from 'react'
import { Layout, Menu } from 'antd'
import {
  HomeOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserAddOutlined,
  ShopOutlined,
  WalletOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  BellOutlined,
  GoogleOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  LockOutlined,
  ScheduleOutlined
} from '@ant-design/icons'
import { RiCoupon3Line, RiNotificationBadgeFill } from 'react-icons/ri'
import { FaMoneyBillWave } from 'react-icons/fa'
import { TbCalendarCancel } from 'react-icons/tb'
import { MessageOutlined, StarOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { PHOTO_URL } from '../utils/apiutils'
import styled from 'styled-components'

/** 選單項目的待處理數徽章 */
const ShopBadge = styled.span`
  display: inline-block;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 8px;
  border-radius: 9px;
  background: #ff4d4f;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  vertical-align: middle;
`

const { Sider } = Layout

const LogoImage = styled.img`
  width: ${(props) => (props.collapsed ? '35px' : '40px')};
  height: ${(props) => (props.collapsed ? '35px' : '40px')};
  border-radius: 10px;
  margin: ${(props) => (props.collapsed ? '10px auto' : '10px')};
`

const BusinessName = styled.div`
  font-size: 14px;
  color: #333;
  margin: 8px;
  text-align: center;
  font-weight: 500;
`

// 可捲動的選單容器：佔滿剩餘高度，內容超出時自動捲動
// 這是修正「側邊欄底部選單被折疊按鈕遮住」的關鍵
const MenuScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0; /* 重要：讓 flex 子元素可以縮小並啟用 overflow */
  overflow-y: auto;
  overflow-x: hidden;

  /* 美化捲軸（不突兀） */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e5e5;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #ccc;
  }
`

// 底部折疊按鈕：正常 flex 子元素（不用 absolute），避免蓋住選單最後幾項
const CollapseBar = styled.div`
  flex: 0 0 auto;
  width: 100%;
  text-align: center;
  padding: 10px 0;
  background: #fff;
  border-top: 1px solid #f0f0f0;
`

const StyledSider = styled(Sider)`
  background: #fff !important;
  /* width: 250px !important;
  min-width: 250px !important; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  /* 外層不捲動，由內部 MenuScroll 區塊負責捲動，避免底部折疊按鈕被蓋 */
  overflow: hidden;

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
    /* 外層也不捲動；捲動交給 MenuScroll */
    overflow: hidden;
  }

  .ant-menu {
    background: transparent;
    border: none;
    padding: 0 12px;
  }

  .ant-menu-item {
    border-radius: 8px;
    margin: 4px 0 !important;
    height: 35px !important;
    line-height: 45px !important;

    &:hover {
      background-color: #f9edf2 !important;
      color: #d5728a !important;
    }

    &.ant-menu-item-selected {
      background-color: #f9edf2 !important;
      color: #d5728a !important;
      font-weight: 600;

      .anticon {
        color: #d5728a !important;
      }
    }
  }
`

// 自動收合的螢幕寬度閾值（≤ 此寬度時 sidebar 預設收合）
const COLLAPSE_BREAKPOINT = 1439

const Sidebar = ({ onCollapse }) => {
  const navigate = useNavigate()
  const location = useLocation()
  // 初始狀態：窄螢幕直接收合
  const [collapsed, setCollapsed] = useState(
    window.innerWidth <= COLLAPSE_BREAKPOINT
  )
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820)
  const [openKeys, setOpenKeys] = useState([])
  // 線上商店待處理訂單數（待核帳 + 待出貨），顯示為選單紅點
  const [shopPendingCount, setShopPendingCount] = useState(0)
  /**
   * 待指派人員的筆數（不指定人員的預約）。
   * 跟線上商店的紅點同一套做法：60 秒輪詢一次就夠，
   * 這只是提示，不需要即時，太頻繁反而增加後端負擔。
   */
  const [pendingAssignCount, setPendingAssignCount] = useState(0)
  const { user, businessRole, permissions } = useAuth()
  const { plan: businessPlan, hasFeature, isBrandPlan, isBusinessOrBrand } =
    useMembership() // 會員方案權限檢查
  const { can } = usePermission()
  const permissionList = Array.isArray(permissions) ? permissions : []
  const isOwner = businessRole === 'owner' || permissionList.includes('*')
  const canViewFinance = can('finance:view')
  // 數據報表用獨立權限：與「結帳訂單／儲值記錄查看」分開
  // owner/manager/accountant 預設有；其他角色由老闆視需要授權
  const canViewReport = can('report:view')

  // 追蹤上一次是否處於窄螢幕，避免在窄範圍內重複覆寫使用者手動展開的偏好
  const prevNarrowRef = useRef(window.innerWidth <= COLLAPSE_BREAKPOINT)

  // 定期更新商店訂單待處理數。60 秒一次即可 ——
  // 這只是紅點提示，不需要即時，太頻繁反而增加後端負擔。
  useEffect(() => {
    let timer = null
    let cancelled = false

    const canSeeShop =
      hasFeature('shopOrder') && (can('shopOrder:view') || can('shopOrder:*'))
    if (!canSeeShop) {
      setShopPendingCount(0)
      return undefined
    }

    const load = async () => {
      try {
        const { getShopOrderStats } = await import('../api/shop')
        const res = await getShopOrderStats()
        if (!cancelled) setShopPendingCount(res.data?.data?.pendingAction || 0)
      } catch (e) {
        /* 徽章失敗不影響選單本身 */
      }
    }

    load()
    timer = setInterval(load, 60000)
    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessPlan, permissions])

  /**
   * 待指派人員的紅點。
   *
   * 所有看得到預約的人都會看到這個數字（提示要傳達得出去），
   * 但真正能指派的只有店主與店長 —— 那一層在預約管理頁與後端各擋一次。
   */
  useEffect(() => {
    let timer = null
    let cancelled = false

    if (!can('booking:view') && !can('booking:*')) {
      setPendingAssignCount(0)
      return undefined
    }

    const load = async () => {
      try {
        const { fetchPendingAssignments } = await import('../api/booking')
        const data = await fetchPendingAssignments()
        if (!cancelled) setPendingAssignCount(data.count || 0)
      } catch (e) {
        /* 徽章失敗不影響選單本身 */
      }
    }

    load()
    timer = setInterval(load, 60000)
    // 指派完成當下立刻重抓一次，不用等下一輪輪詢（見 AssignStylistModal.js）
    window.addEventListener('pending-assignment-changed', load)
    return () => {
      cancelled = true
      window.removeEventListener('pending-assignment-changed', load)
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions])

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const nowNarrow = width <= COLLAPSE_BREAKPOINT
      setIsMobile(width <= 820)
      // 只在跨越 1440 邊界時自動切換收合狀態：
      //  - 從寬變窄 → 自動收合
      //  - 從窄變寬 → 自動展開
      // 在同一範圍內 resize 時不覆蓋使用者的手動偏好
      if (nowNarrow !== prevNarrowRef.current) {
        setCollapsed(nowNarrow)
        prevNarrowRef.current = nowNarrow
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // 根據當前路徑自動打開對應的父菜單（只打開一個）
    let newOpenKey = null
    if (location.pathname.startsWith('/value-added')) {
      newOpenKey = '/value-added'
    } else if (location.pathname.startsWith('/class-ticket')) {
      newOpenKey = '/class-ticket'
    } else if (location.pathname.startsWith('/settings')) {
      newOpenKey = '/settings'
    } else if (location.pathname.startsWith('/checkout')) {
      newOpenKey = '/checkout'
    } else if (location.pathname.startsWith('/commission')) {
      newOpenKey = '/commission'
    } else if (
      location.pathname.startsWith('/inventory') ||
      location.pathname === '/inventory/purchase' ||
      location.pathname === '/inventory/purchase-records'
    ) {
      newOpenKey = '/inventory'
    } else if (
      location.pathname.startsWith('/employees/') ||
      location.pathname === '/employees'
    ) {
      newOpenKey = '/employees'
    } else if (
      location.pathname.startsWith('/customers') ||
      location.pathname === '/customers'
    ) {
      newOpenKey = '/customers'
    } else if (location.pathname.startsWith('/revisit-reminders')) {
      newOpenKey = '/customers'
    }
    setOpenKeys(newOpenKey ? [newOpenKey] : [])
  }, [location.pathname])

  const handleOpenChange = (keys) => {
    // 只保留最後打開的菜單，關閉其他所有菜單
    const latestOpenKey =
      keys.find((key) => !openKeys.includes(key)) || keys[keys.length - 1]
    setOpenKeys(latestOpenKey ? [latestOpenKey] : [])
  }

  useEffect(() => {
    onCollapse(collapsed)
  }, [collapsed, onCollapse])

  // 員工菜單：目前只有一個子項，直接作為單一菜單項，不使用 children
  // 如果未來需要多個子項，可以改為使用 children 結構

  // 店長以上 = owner 或具備 finance:manage 權限
  // 抽成設定屬於商業敏感配置，僅店長以上可看
  const canManageCommission = isOwner || can('finance:manage')

  const commissionChildren = canManageCommission
    ? [
        {
          key: '/commission/salary',
          label: '薪資設定'
        },
        {
          key: '/commission/monthly-salary',
          label: '每月員工薪資'
        },
        {
          key: '/commission/allowance',
          label: '零用金設定'
        },
        {
          key: '/commission/performance',
          label: '技術服務抽成設定'
        },
        {
          key: '/commission/service-rules',
          label: '服務項目抽成設定'
        },
        {
          key: '/commission/value-added',
          label: '儲值抽成設定'
        },
        {
          key: '/commission/product-sales',
          label: '產品銷售抽成設定'
        },
        {
          key: '/commission/class-ticket',
          label: '堂票銷售抽成設定'
        }
      ]
    : [
        {
          key: '/commission/monthly-salary',
          label: '我的薪資'
        },
        {
          key: '/commission/allowance',
          label: '零用金紀錄'
        }
      ]

  const commissionMenuItems =
    commissionChildren && commissionChildren.length > 0 && hasFeature('salary')
      ? [
          {
            key: '/commission',
            icon: <DollarOutlined />,
            label: isOwner ? '薪水與業績管理' : '薪資與福利',
            children: commissionChildren
          }
        ]
      : []

  const mainMenuItems = [
    { key: '/appointments', icon: <HomeOutlined />, label: '首頁' },
    {
      key: '/booking-detail',
      icon: <CalendarOutlined />,
      label:
        pendingAssignCount > 0 ? (
          <span>
            預約管理
            <ShopBadge>
              {pendingAssignCount > 99 ? '99+' : pendingAssignCount}
            </ShopBadge>
          </span>
        ) : (
          '預約管理'
        )
    },
    // 數據報表（純報表）走 report:view；
    // 「營收紀錄查詢」屬於檢視結帳/交易紀錄，沿用 finance:view。
    ...(canViewReport || (canViewFinance && hasFeature('checkout'))
      ? [
          {
            key: '/dashboard',
            icon: <DashboardOutlined />,
            label: '數據報表',
            children: [
              ...(canViewReport
                ? [
                    {
                      key: '/dashboard',
                      label: '數據報表'
                    }
                  ]
                : []),
              ...(canViewFinance && hasFeature('checkout')
                ? [
                    {
                      key: '/dashboard/revenue-records',
                      label: '營收紀錄查詢'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    {
      key: '/customers',
      icon: <UserOutlined />,
      label: '顧客管理',
      children: [
        {
          key: '/customers',
          icon: <UserOutlined />,
          label: '顧客列表'
        },
        ...(hasFeature('customMembership')
          ? [
              {
                key: '/customers/membership-tiers',
                icon: <CrownOutlined />,
                label: '會員等級設置'
              },
              {
                key: '/customers/groups',
                icon: <TeamOutlined />,
                label: '顧客群組'
              }
            ]
          : []),
        ...(hasFeature('revisitReminder')
          ? [
              {
                key: '/customers/revisit-reminders',
                icon: <BellOutlined />,
                label: '回訪提醒'
              }
            ]
          : [])
      ]
    },
    // 顧客評價：全方案開放，但要有 review:view（設計師以上預設都有）
    ...(hasFeature('review') &&
    (can('review:view') || can('review:manage') || can('review:reply_own'))
      ? [{ key: '/reviews', icon: <StarOutlined />, label: '顧客評價' }]
      : []),
    { key: '/coupons', icon: <RiCoupon3Line />, label: '優惠模組' },
    { key: '/products', icon: <AppstoreOutlined />, label: '服務' },
    { key: '/employees', icon: <TeamOutlined />, label: '員工' },
    // 班表：所有方案都能用，但要有 schedule:view（各角色預設都有）
    ...(hasFeature('schedule') && can('schedule:view')
      ? [{ key: '/schedule', icon: <ScheduleOutlined />, label: '班表' }]
      : []),
    ...(hasFeature('recruitment')
      ? [
          {
            key: '/recruitment-dashboard',
            icon: <UserAddOutlined />,
            label: '招聘管理'
          }
        ]
      : []),
    // 儲值管理：方案要開通 + 至少有一個子項權限才顯示
    ...(hasFeature('storedValue') &&
    (can('balance:topup') ||
      can('finance:view') ||
      can('finance:manage') ||
      can('finance:*'))
      ? [
          {
            key: '/value-added',
            icon: <WalletOutlined />,
            label: '儲值管理',
            children: [
              // 儲值購買：需要 balance:topup 或 finance:manage
              ...(can('balance:topup') ||
              can('finance:manage') ||
              can('finance:*')
                ? [
                    {
                      key: '/value-added/purchase',
                      label: '儲值購買'
                    }
                  ]
                : []),
              // 剩餘儲值金 / 儲值記錄：需要 finance:view 或更高
              ...(can('finance:view') ||
              can('finance:manage') ||
              can('finance:*')
                ? [
                    {
                      key: '/value-added/remaining-balance',
                      label: '剩餘儲值金查詢'
                    },
                    {
                      key: '/value-added/records',
                      label: '儲值記錄查詢'
                    },
                    {
                      key: '/value-added/consumption',
                      label: '儲值消費紀錄'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    ...(hasFeature('classTicket')
      ? [
          {
            key: '/class-ticket',
            icon: <IdcardOutlined />,
            label: '堂票模組',
            children: [
              {
                key: '/class-ticket/settings',
                label: '堂票設定'
              },
              {
                key: '/class-ticket/records',
                label: '堂票紀錄列表'
              }
            ]
          }
        ]
      : []),
    ...(hasFeature('consentForm')
      ? [
          {
            key: '/consent-form',
            icon: <FileProtectOutlined />,
            label: '同意書模組',
            children: [
              {
                key: '/consent-form/settings',
                label: '同意書設定'
              },
              {
                key: '/consent-form/records',
                label: '簽署紀錄'
              }
            ]
          }
        ]
      : []),
    ...commissionMenuItems,
    // 結帳管理
    ...(hasFeature('checkout')
      ? [
          {
            key: '/checkout',
            icon: <CheckCircleOutlined />,
            label: '結帳管理',
            children: [
              {
                key: '/checkout/checkout',
                label: '結帳'
              },
              {
                key: '/checkout/records',
                label: '結帳記錄查詢'
              },
              // 分期收款：方案 + 權限都有才顯示。放在結帳記錄後面 ——
              // 店家的動線是「查單 → 發現有分期 → 去收款」
              ...(hasFeature('installment') &&
              (can('installment:view') || can('installment:manage'))
                ? [
                    {
                      key: '/checkout/installments',
                      label: '分期收款'
                    }
                  ]
                : []),
              {
                key: '/checkout/employee-order',
                label: '員工自購或領料打單'
              },
              {
                key: '/checkout/employee-records',
                label: '員工自購或領料記錄'
              }
            ]
          }
        ]
      : []),
    // 支付紀錄（信用卡交易管理）
    // {
    //   key: '/payment-records',
    //   icon: <CreditCardOutlined />,
    //   label: '支付紀錄'
    // },
    // 庫存管理：方案要開通 + 至少有一個子頁權限才顯示整個 menu
    // 各子頁依需要的權限獨立顯示／隱藏
    ...(hasFeature('inventory') &&
    (can('inventory:view') || can('inventory:manage') || can('inventory:*'))
      ? [
          {
            key: '/inventory',
            icon: <ShoppingCartOutlined />,
            label: '庫存管理',
            children: [
              // 新增/採購、進貨管理、拆公用：需要 inventory:manage
              ...(can('inventory:manage') || can('inventory:*')
                ? [
                    {
                      key: '/inventory/products/add',
                      label: '新增/採購商品'
                    },
                    {
                      key: '/inventory/purchase',
                      label: '進貨管理'
                    }
                  ]
                : []),
              // 進貨紀錄、商品管理、使用記錄、拆公用：需要 inventory:view 或以上
              // 拆公用允許員工執行（成本欄位會在頁內依 inventory:manage 控制是否可編輯）
              ...(can('inventory:view') ||
              can('inventory:manage') ||
              can('inventory:*')
                ? [
                    {
                      key: '/inventory/purchase-records',
                      label: '進貨紀錄'
                    },
                    {
                      key: '/inventory/products',
                      label: '商品管理'
                    },
                    {
                      key: '/inventory/usage',
                      label: '使用記錄'
                    },
                    {
                      key: '/inventory/public-use',
                      label: '拆公用'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
       // 線上商店：方案要開通 + 至少有一個子頁權限才顯示整個 menu
    ...(hasFeature('shopOrder') &&
    (can('shopOrder:view') ||
      can('shopOrder:manage') ||
      can('shopSetting:manage') ||
      can('shopOrder:*'))
      ? [
          {
            key: '/shop',
            icon: <ShopOutlined />,
            label:
              shopPendingCount > 0
                ? (
                  <span>
                    線上商店
                    <ShopBadge>{shopPendingCount > 99 ? '99+' : shopPendingCount}</ShopBadge>
                  </span>
                  )
                : (
                    '線上商店'
                  ),
            children: [
              // 訂單：只要有 shopOrder:view 就看得到（會計對帳、設計師查詢）
              ...(can('shopOrder:view') || can('shopOrder:*')
                ? [
                    {
                      key: '/shop/orders',
                      label:
                        shopPendingCount > 0
                          ? (
                            <span>
                              商店訂單
                              <ShopBadge>
                                {shopPendingCount > 99 ? '99+' : shopPendingCount}
                              </ShopBadge>
                            </span>
                            )
                          : (
                              '商店訂單'
                            )
                    }
                  ]
                : []),
              // 取貨核銷：櫃檯用，需要改狀態的權限
              ...(can('shopOrder:manage') || can('shopOrder:*')
                ? [{ key: '/shop/pickup', label: '取貨核銷' }]
                : []),
              // 上架與設定：限 shopSetting:manage（櫃檯只處理訂單，不改設定）
              ...(can('shopSetting:manage') || can('shopOrder:*')
                ? [
                    { key: '/shop/products', label: '上架商品' },
                    { key: '/shop/settings', label: '商店設定' }
                  ]
                : [])
            ]
          }
        ]
      : []),
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '設定',
      children: [
        {
          key: '/settings/info',
          icon: <HomeOutlined />,
          label: '店家資訊'
        },
        {
          key: '/settings/hours',
          icon: <ClockCircleOutlined />,
          label: '營業時間'
        },
        {
          key: '/settings/daysOff',
          icon: <CalendarOutlined />,
          label: '休假日設定'
        },
        {
          key: '/settings/appointment',
          icon: <SettingOutlined />,
          label: '預約設定'
        },
        {
          key: '/settings/deposit',
          icon: <FaMoneyBillWave />,
          label: '定金設定'
        },
        {
          key: '/settings/booking',
          icon: <RiNotificationBadgeFill />,
          label: '預約須知'
        },
        {
          key: '/settings/cancel',
          icon: <TbCalendarCancel />,
          label: '取消規定'
        },
        {
          key: '/settings/line',
          icon: <MessageOutlined />,
          label: 'LINE 整合'
        },
        {
          key: '/settings/myLineNotify',
          icon: <MessageOutlined />,
          label: '我的 LINE 通知'
        },
        ...(isBusinessOrBrand()
          ? [
              {
                key: '/settings/calendar',
                icon: <GoogleOutlined />,
                label: 'Google 日曆同步'
              }
            ]
          : []),
        {
          key: '/settings/membership',
          icon: <CrownOutlined />,
          label: '會員資訊'
        },
        {
          key: '/settings/payment',
          icon: <CreditCardOutlined />,
          label: '金流設定'
        },
        // 金流手續費：這裡可以設定費率與「向客人加收服務費」，影響整店的收費方式，
        // 預設只有店主與店長看得到入口（後端另有 paymentFee:manage 把關）
        ...(can('paymentFee:manage')
          ? [
              {
                key: '/settings/paymentFee',
                icon: <CreditCardOutlined />,
                label: '金流手續費'
              }
            ]
          : []),
        {
          key: '/settings/password',
          icon: <LockOutlined />,
          label: '修改密碼'
        }
      ]
    }
  ]

  const onMenuClick = (key) => {
    navigate(key)
  }

  const Logo = (
    <div
      style={{
        textAlign: 'center',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <LogoImage
        src={
          user?.image?.[0]
            ? `${PHOTO_URL}${user.image[0].url}`
            : '/logo192.png'
        }
        alt='Logo'
        collapsed={collapsed}
      />
      {!collapsed && <BusinessName>{user.name || '店家管理'}</BusinessName>}
    </div>
  )

  if (isMobile) {
    return null // 在移動端不渲染 Sidebar
  }

  return (
    <StyledSider
      data-tour='sidebar'
      width={280}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      style={{
        background: '#fff',
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000
      }}
      trigger={null} // 添加这一行以移除默认的触发器
    >
      {Logo}
      <MenuScroll>
        <Menu
          mode='inline'
          selectedKeys={[location.pathname]}
          // 收合模式下不能控制 openKeys：收合時 antd 會把「打開的子選單」
          // 渲染成懸浮彈出框，若仍以受控 openKeys 強制展開（依路徑自動展開的
          // effect），彈出框會永久釘在畫面上、蓋住內容（客戶回報的結帳頁問題）。
          // 收合時改為非受控 → 彈出框只在滑鼠懸停時出現、移開即收合。
          {...(collapsed ? {} : { openKeys, onOpenChange: handleOpenChange })}
          style={{ borderRight: 0, background: '#fff' }}
          onClick={({ key }) => onMenuClick(key)}
          items={mainMenuItems}
        />
      </MenuScroll>
      <CollapseBar>
        {React.createElement(
          collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
          {
            onClick: () => setCollapsed(!collapsed),
            style: { fontSize: '18px', cursor: 'pointer' }
          }
        )}
      </CollapseBar>
    </StyledSider>
  )
}

export default Sidebar
